import React, { useEffect, useState } from "react";
import styles from "./NotePanel.module.css";
import {
  BlueButton,
  FindmorePanel,
  WhiteButton,
} from "@react-solutions-ui/findmore-ui";

import {
  createEmployeeNote,
  updateEmployeeNote,
  getEmployeeNotesCategory,
  IEmployeeNote,
} from "../../../../features/home/services/employeeNotesService";

export interface Props {
  mode: "create" | "edit" | "view";
  user: string;
  employeeId: string;
  onClose: () => void;
  onSuccess?: (message: string) => void;
  onSuccessEdit?: (
    message: string,
    noteid: string,
    notetitle: string,
    notetext: string,
    notecategory: string
  ) => void;

  noteId?: string;
  initialSubject?: string;
  initialMessage?: string;
  initialCategoryName?: string;

  onChangeMode?: (mode: "create" | "edit" | "view") => void;
}

export default function NotePanel({
  mode,
  user,
  employeeId,
  onClose,
  onSuccess,
  onSuccessEdit,
  noteId,
  initialSubject = "",
  initialMessage = "",
  initialCategoryName = "",
  onChangeMode,
}: Props) {
  const [subject, setSubject] = useState<string>(initialSubject);
  const [message, setMessage] = useState<string>(initialMessage);
  const [notesCategories, setNotesCategories] = useState<IEmployeeNote[]>([]);
  const [selectedCategory, setSelectedCategory] =
    useState<string>(initialCategoryName);
  const [isSending, setIsSending] = useState<boolean>(false);

  const isFormValid =
    subject.trim() !== "" &&
    message.trim() !== "" &&
    selectedCategory.trim() !== "";

  useEffect(() => {
    getEmployeeNotesCategory().then((categories) => {
      setNotesCategories(categories);
    });
  }, []);

  const handleSubmit = async () => {
    if (!isFormValid) return;
    try {
      setIsSending(true);
      const categoryId = notesCategories.find(
        (c) => c.name === selectedCategory
      )?.id;
      if (!categoryId) throw new Error("Categoria inválida");

      if (mode === "create") {
        await createEmployeeNote(employeeId, subject, message, categoryId);
        onSuccess?.("Nota adicionada com sucesso!");
      } else if (mode === "edit" && noteId) {
        await updateEmployeeNote(
          employeeId,
          noteId,
          subject,
          message,
          categoryId
        );
        if (onSuccessEdit) {
          onSuccessEdit(
            "Nota atualizada com sucesso!",
            noteId,
            subject,
            message,
            selectedCategory
          );
        } else {
          onSuccess?.("Nota atualizada com sucesso!");
        }
      }
      onClose();
    } catch (error: any) {
      console.error(error);
      alert(error.message || "Ocorreu um erro ao guardar a nota.");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <FindmorePanel open onClose={onClose} title="NOTA">
      <div className={styles.content}>
          <div className={styles.fieldsContainer}>
            <div className={styles.subtitle}>
              {mode === "create" && (
                <>Registar observação sobre o colaborador {user}</>
              )}
              {(mode === "edit" || mode === "view") && (
                <>Observação registada sobre o colaborador {user}</>
              )}
            </div>

            <div className={styles.fieldGroup}>
              <label>
                {mode === "view" ? (
                  <div className={styles.labelReadonly}>Categoria:</div>
                ) : (
                  <>
                    <span className={styles.required}>*</span> Categoria
                  </>
                )}
              </label>
              {mode === "view" ? (
                <span
                  className={`${styles.categoryBadge} ${styles.categoryBadgeSelected}`}
                  data-category={selectedCategory.toUpperCase()}
                >
                  {selectedCategory}
                </span>
              ) : (
                <div className={styles.categoriesList}>
                  {notesCategories.map((category) => {
                    const isSelected = selectedCategory === category.name;
                    return (
                      <button
                        key={category.id}
                        type="button"
                        className={`${styles.categoryBadge} ${
                          isSelected ? styles.categoryBadgeSelected : ""
                        }`}
                        data-category={category.name.toUpperCase()}
                        aria-pressed={isSelected}
                        onClick={() => setSelectedCategory(category.name)}
                      >
                        {category.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className={styles.fieldGroup}>
              <label>
                {mode === "view" ? (
                  <div className={styles.labelReadonly}>Assunto:</div>
                ) : (
                  <>
                    <span className={styles.required}>*</span> Assunto
                  </>
                )}
              </label>
              {mode === "view" ? (
                <div className={styles.readonlyText}>{subject}</div>
              ) : (
                <input
                  value={subject}
                  placeholder="Assunto"
                  onChange={(e) => setSubject(e.target.value)}
                />
              )}
            </div>

            <div className={styles.fieldGroup}>
              <label>
                {mode === "view" ? (
                  <div className={styles.labelReadonly}>Mensagem:</div>
                ) : (
                  <>
                    <span className={styles.required}>*</span> Mensagem
                  </>
                )}
              </label>
              {mode === "view" ? (
                <div className={styles.readonlyText}>{message}</div>
              ) : (
                <textarea
                  value={message}
                  placeholder="Mensagem"
                  onChange={(e) => setMessage(e.target.value)}
                />
              )}
            </div>
          </div>

          {mode === "create" && (
            <div className={styles.infoText}>
              Esta nota será visível apenas para o team leader e para a equipa
              de management.
            </div>
          )}

          <div className={styles.buttons}>
            <WhiteButton
              text={mode === "view" ? "FECHAR" : "CANCELAR"}
              onClick={onClose}
            />

            <BlueButton
              text={
                mode === "create"
                  ? "ADICIONAR NOTA"
                  : mode === "edit"
                  ? "GUARDAR"
                  : "EDITAR"
              }
              onClick={() => {
                if (mode === "view") {
                  onChangeMode?.("edit");
                } else {
                  handleSubmit();
                }
              }}
              disabled={(!isFormValid && mode !== "view") || isSending}
              isSending={isSending}
            />
          </div>
      </div>
    </FindmorePanel>
  );
}
